import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import './PricingSection.css';

const PricingSection: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <section className="pricing-section">
      <div className="pricing-background" />
      
      <div className="container">
        <div className="pricing-container">
          
          {/* Section Header */}
          <div className="pricing-header">
            <div className="pricing-badge">
              <div className="pricing-badge-dot" />
              Pricing Plans
            </div>
            
            <h2 className="pricing-title">Choose Your Security Level</h2>
            
            <p className="pricing-subtitle">
              Flexible pricing plans designed to scale with your business needs and security requirements
            </p>
          </div>
          
          {/* Pricing Cards */}
          <div className="pricing-grid">
            
            {/* Starter Plan */}
            <div className="pricing-card">
              <div className="pricing-card-header">
                <div className="pricing-card-icon">
                  <i className="ri-shield-line"></i>
                </div>
                <h3 className="pricing-card-title">Starter</h3>
                <p className="pricing-card-description">Perfect for small businesses getting started with cybersecurity</p>
                {isAuthenticated && (
                  <div className="pricing-card-price">
                    <span className="pricing-price">$99</span>
                    <span className="pricing-period">/month</span>
                  </div>
                )}
              </div>
              
              <div className="pricing-card-features">
                <div className="pricing-feature">
                  <i className="ri-check-line"></i>
                  <span>Up to 10 devices monitored</span>
                </div>
                <div className="pricing-feature">
                  <i className="ri-check-line"></i>
                  <span>Basic threat detection</span>
                </div>
                <div className="pricing-feature">
                  <i className="ri-check-line"></i>
                  <span>Email alerts</span>
                </div>
                <div className="pricing-feature">
                  <i className="ri-check-line"></i>
                  <span>Monthly reports</span>
                </div>
                <div className="pricing-feature">
                  <i className="ri-check-line"></i>
                  <span>Business hours support</span>
                </div>
              </div>
              
                <Link to="/register" className="pricing-btn pricing-btn-secondary">
                  <span>Start Free Trial</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
            </div>
            
            {/* Professional Plan - Featured */}
            <div className="pricing-card pricing-card-featured">
              <div className="pricing-card-badge">Most Popular</div>
              <div className="pricing-card-header">
                <div className="pricing-card-icon">
                  <i className="ri-shield-check-line"></i>
                </div>
                <h3 className="pricing-card-title">Professional</h3>
                <p className="pricing-card-description">Advanced protection for growing businesses with comprehensive features</p>
                {isAuthenticated && (
                  <div className="pricing-card-price">
                    <span className="pricing-price">$299</span>
                    <span className="pricing-period">/month</span>
                  </div>
                )}
              </div>
              
              <div className="pricing-card-features">
                <div className="pricing-feature">
                  <i className="ri-check-line"></i>
                  <span>Up to 100 devices monitored</span>
                </div>
                <div className="pricing-feature">
                  <i className="ri-check-line"></i>
                  <span>AI-powered threat detection</span>
                </div>
                <div className="pricing-feature">
                  <i className="ri-check-line"></i>
                  <span>Real-time alerts</span>
                </div>
                <div className="pricing-feature">
                  <i className="ri-check-line"></i>
                  <span>Weekly detailed reports</span>
                </div>
                <div className="pricing-feature">
                  <i className="ri-check-line"></i>
                  <span>24/7 priority support</span>
                </div>
                <div className="pricing-feature">
                  <i className="ri-check-line"></i>
                  <span>Incident response team</span>
                </div>
                <div className="pricing-feature">
                  <i className="ri-check-line"></i>
                  <span>Vulnerability assessments</span>
                </div>
              </div>
              
                <Link to="/register" className="pricing-btn pricing-btn-primary">
                  <span>Start Free Trial</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
            </div>
            
            {/* Enterprise Plan */}
            <div className="pricing-card">
              <div className="pricing-card-header">
                <div className="pricing-card-icon">
                  <i className="ri-shield-star-line"></i>
                </div>
                <h3 className="pricing-card-title">Enterprise</h3>
                <p className="pricing-card-description">Maximum security for large organizations with custom requirements</p>
                {isAuthenticated && (
                  <div className="pricing-card-price">
                    <span className="pricing-price">Custom</span>
                    <span className="pricing-period">pricing</span>
                  </div>
                )}
              </div>
              
              <div className="pricing-card-features">
                <div className="pricing-feature">
                  <i className="ri-check-line"></i>
                  <span>Unlimited devices monitored</span>
                </div>
                <div className="pricing-feature">
                  <i className="ri-check-line"></i>
                  <span>Custom AI model training</span>
                </div>
                <div className="pricing-feature">
                  <i className="ri-check-line"></i>
                  <span>Instant alerts & notifications</span>
                </div>
                <div className="pricing-feature">
                  <i className="ri-check-line"></i>
                  <span>Real-time dashboards</span>
                </div>
                <div className="pricing-feature">
                  <i className="ri-check-line"></i>
                  <span>Dedicated security team</span>
                </div>
                <div className="pricing-feature">
                  <i className="ri-check-line"></i>
                  <span>On-site incident response</span>
                </div>
                <div className="pricing-feature">
                  <i className="ri-check-line"></i>
                  <span>Compliance management</span>
                </div>
                <div className="pricing-feature">
                  <i className="ri-check-line"></i>
                  <span>Custom integrations</span>
                </div>
              </div>
              
                <a href="#contact" className="pricing-btn pricing-btn-secondary">
                  <span>Contact Sales</span>
                  <i className="ri-phone-line"></i>
                </a>
            </div>
            
          </div>
          
          {/* Terms Text */}
          <div className="pricing-terms">
            <p className="pricing-terms-text">
              By selecting a plan, you agree to our <Link to="/terms-of-service">Terms of Service</Link> and <Link to="/privacy-policy">Privacy Policy</Link>. 
              All plans include a 14-day free trial with no commitment required.
            </p>
          </div>
          
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
